import { useCallback, useEffect, useMemo, useState } from "react";
import { None, Option } from "space-monad";
import styled from "styled-components";
import { Flex } from "../layout";
import { WIDGET_BORDER_RADIUS } from "../widget";
import { VerificationService } from "../../store/verification/service";

export const IMAGE_SIZE = 194;
export const PADDING_SIZE = 12;
export const BORDER_SIZE = 2;

export const Content = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 1em;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border-radius: 3px;
  font-weight: 500;
`;

export const Container = styled.div`
  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: ${IMAGE_SIZE + PADDING_SIZE + BORDER_SIZE}px;
  height: ${IMAGE_SIZE + PADDING_SIZE + BORDER_SIZE}px;
  padding: ${PADDING_SIZE}px;
  border: ${BORDER_SIZE}px solid #ccc;
  border-radius: ${WIDGET_BORDER_RADIUS};
  cursor: pointer;

  ${Content} {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  :hover ${Content} {
    opacity: 1;
  }
`;

export function useKycImage(
  imageUrl: Option<{
    docId: string;
    kycId: string;
  }>
) {
  const [currentImage, setCurrentImage] = useState<
    Option<{
      data: string;
      type: string | null;
    }>
  >(None);

  useEffect(() => {
    imageUrl.fold(
      () => {},
      (data) =>
        VerificationService.getKycDocument(data).then((x) => {
          setCurrentImage(x);
        })
    );
  }, [imageUrl]);

  return currentImage;
}

export const usePdf = () => {
  const [imageHeight, setImageHeight] = useState<number>(0);
  const [imageWidth, setImageWidth] = useState<number>(0);

  const [pageNumber, setPageNumber] = useState(1);

  const [pdf, setPdf] = useState<any>();

  useEffect(() => {
    setPageNumber(1);
  }, [pdf]);

  const numPages = useMemo(() => (pdf ? pdf.numPages : 0), [pdf]);

  useEffect(() => {
    pdf?.getPage(pageNumber).then((page: any) => {
      const viewport = page.getViewport({ scale: 1 });
      setImageHeight(viewport.viewBox[3]);
      setImageWidth(viewport.viewBox[2]);
    });
  }, [pdf, pageNumber]);

  const onLoad = useCallback(
    async (pdf: any) => {
      setPdf(pdf);
    },
    [setPdf]
  );

  const reset = useCallback(() => {
    setImageHeight(0);
    setImageWidth(0);
  }, [setImageHeight, setImageWidth]);

  return {
    reset,
    onLoad,
    imageHeight,
    imageWidth,
    numPages,
    pageNumber,
    setPageNumber,
  };
};
